/** The CSS generic families. These are keywords, and quoting one turns it into a family name. */
export const CSS_GENERIC_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace'
])

/**
 * A family name may be written unquoted only when every space-separated word is a CSS identifier,
 * and an identifier cannot start with a digit. `Source Sans 3`, `Baloo 2` and `M PLUS 1p` all fail
 * that test.
 */
const CSS_IDENTIFIER = /^[a-z_-][\w-]*$/i

function isUnquotedFamilyName(family: string): boolean {
  return family.split(/\s+/).every((word) => CSS_IDENTIFIER.test(word))
}

function isAlreadyQuoted(family: string): boolean {
  return (family.startsWith('"') && family.endsWith('"')) || (family.startsWith("'") && family.endsWith("'"))
}

/**
 * Quotes one family name if CSS requires it.
 *
 * This matters more than it looks: an invalid family name doesn't just fail on its own, it makes
 * the whole `font-family` declaration invalid, so the browser throws away every fallback in the
 * stack too. A style set to Source Sans 3 rendered in the browser's default face — the font had
 * loaded, the declaration was simply discarded. (Same grammar rule that makes
 * `doc.fonts.check('12px source sans 3')` throw; see checkFontFamily in loadPresentationFonts.)
 */
export function quoteFontFamily(family: string): string {
  const trimmed = family.trim()
  if (!trimmed || isAlreadyQuoted(trimmed)) return trimmed
  if (CSS_GENERIC_FAMILIES.has(trimmed.toLowerCase())) return trimmed
  if (isUnquotedFamilyName(trimmed)) return trimmed
  return `"${trimmed.replace(/"/g, '')}"`
}

/**
 * Makes a stored font stack (`'source sans 3, sans-serif'`) safe to emit as CSS, quoting the family
 * names that need it. Use this anywhere a stored `fontFamily` value reaches a stylesheet or an
 * inline style.
 */
export function toCssFontStack(value: string): string {
  if (typeof value !== 'string') return value
  return value
    .split(',')
    .map((part) => quoteFontFamily(part))
    .filter(Boolean)
    .join(', ')
}
